import type { RegionalTransfer } from "../Components/Documents/ViewDocs/Documents.types";

/**
 * Add timestamp and username to filename to prevent conflicts
 * @param fileName - Original filename (e.g., "ReleaseNotes.pdf")
 * @param userName - Username without domain (e.g., "hnaqvi")
 * @returns Timestamped filename (e.g., "ReleaseNotes_hnaqvi_2026-01-30T002107784Z.pdf")
 *
 * Pattern: {filename}_{username}_{timestamp}.{extension}
 * Timestamp format: ISO 8601 with punctuation removed (e.g., 2026-01-30T002107784Z)
 */
export const addTimeStampToFileName = (
  fileName: string,
  userName: string,
): string => {
  const currentTimeStamp = new Date();
  const fileParts = fileName.split(".");
  const fileNameWithoutExt = fileParts.slice(0, -1).join("."); // Handle filenames with multiple dots
  const extension = fileParts[fileParts.length - 1];

  // ISO timestamp with punctuation removed (matches legacy format)
  const timestamp = currentTimeStamp
    .toISOString()
    .replace(/\./g, "") // Remove dots
    .replace(/:/g, ""); // Remove colons

  return `${fileNameWithoutExt}_${userName}_${timestamp}.${extension}`;
};

/**
 * Format user LoginName to Windows NT format required by PBS Enterprise API
 * @param loginName - User LoginName from UserContext (e.g., "hnaqvi" or "POSITIVE\\hnaqvi")
 * @returns Windows NT formatted username (e.g., "POSITIVE\\hnaqvi")
 *
 * This centralized function replaces scattered logic throughout components
 */
export const formatUserNameForAPI = (loginName?: string): string => {
  if (!loginName) {
    return "";
  }

  // If already in Windows NT format, return as is
  if (loginName.includes("\\")) {
    return loginName;
  }

  // Add POSITIVE domain prefix
  return `POSITIVE\\${loginName}`;
};

/**
 * Trigger a browser download for a Blob using a temporary anchor element
 * @param blob - File contents to save
 * @param fileName - Suggested file name for the download
 */
export const downloadBlob = (blob: Blob, fileName: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const addDays = (date: any, days: any) => {
  var result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

/**
 * Format date string to MM/DD/YYYY format (matches legacy Utility.getSimpleDateFormat)
 * @param dateString - ISO date string (e.g., "2026-01-21T05:57:17.048Z")
 * @returns Formatted date string (e.g., "01/21/2026") or empty string if invalid
 */
export const getSimpleDateFormat = (
  dateString: string | undefined | null,
): string => {
  if (!dateString) return "";

  try {
    // Handle ISO date strings: extract YYYY-MM-DD portion
    const datePart = dateString.slice(0, 10);
    const parts = datePart.split("-");

    if (parts.length === 3) {
      // Format: MM/DD/YYYY
      return `${parts[1]}/${parts[2]}/${parts[0]}`;
    }

    // Fallback: try parsing as Date
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    }

    return "";
  } catch (error) {
    return "";
  }
};

/**
 * Get a date as a local calendar date string
 * @param date - Date to format (defaults to now)
 * @returns Local date in YYYY-MM-DD format (e.g., "2026-10-01")
 */
export const getLocalDateString = (date: Date = new Date()): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Pick the regional transfer that should drive the Documents region toggle
 * A row qualifies when Status is "Approved", it has a Transfering To region, and its
 * Expected Transfer Date is strictly after today (a transfer dated today does not count).
 * When several rows qualify, the earliest Expected Transfer Date wins.
 * @param items - Raw rows from the "User Regional Transfers" list query
 * @param today - Local date in YYYY-MM-DD format (see getLocalDateString)
 * @returns The qualifying transfer, or null when none qualifies
 */
export const getQualifyingRegionalTransfer = (
  items: any[],
  today: string,
): RegionalTransfer | null => {
  if (!Array.isArray(items)) return null;

  const transfers = items
    .filter((item) => item?.Status === "Approved")
    .map((item) => {
      // Lookup columns come back as { LookupId, LookupValue }; accept a plain string too
      const toRegion = item.Transferring_x0020_To;
      const newRegion =
        typeof toRegion === "string" ? toRegion : toRegion?.LookupValue;
      const toState = item.New_x0020_State;
      const newState =
        typeof toState === "string" ? toState : toState?.LookupValue;
      const expectedTransferDate = item.Expected_x0020_Transfer_x0020_Da;

      return {
        id: item.ID,
        newRegion: typeof newRegion === "string" ? newRegion.trim() : "",
        newState: typeof newState === "string" ? newState.trim() : "",
        newStateCode: "", // Resolved separately from the States list (getStateCode)
        expectedTransferDate:
          typeof expectedTransferDate === "string" ? expectedTransferDate : "",
      };
    })
    // Compare the YYYY-MM-DD portion, matching how getSimpleDateFormat displays it
    .filter(
      (transfer) =>
        transfer.newRegion &&
        transfer.expectedTransferDate &&
        transfer.expectedTransferDate.slice(0, 10) > today,
    )
    .sort((a, b) =>
      a.expectedTransferDate
        .slice(0, 10)
        .localeCompare(b.expectedTransferDate.slice(0, 10)),
    );

  return transfers[0] ?? null;
};

/**
 * Resolve a US state name to its 2-letter code using rows from the States list
 * The credentialing API only accepts 2-letter codes, but the User Regional Transfers
 * list stores the full name. A value that is already a 2-letter code is returned as-is.
 * @param stateRows - Rows from GET /api/states (each has a Code field)
 * @param state - State name or code (e.g., "California" or "CA")
 * @returns Upper-case 2-letter code, or "" when it can't be resolved
 */
export const getStateCode = (stateRows: any[], state: string): string => {
  const value = state?.trim() ?? "";
  if (!value) return "";
  if (/^[A-Za-z]{2}$/.test(value)) return value.toUpperCase();
  if (!Array.isArray(stateRows)) return "";

  const target = value.toLowerCase();
  // The row's name field isn't fixed, so match any text field and take its Code
  const match = stateRows.find(
    (row) =>
      typeof row?.Code === "string" &&
      Object.entries(row).some(
        ([key, field]) =>
          key !== "Code" &&
          typeof field === "string" &&
          field.trim().toLowerCase() === target,
      ),
  );

  return match ? match.Code.trim().toUpperCase() : "";
};

/**
 * Format bytes to human-readable format (KB, MB, GB)
 * @param bytes - Number of bytes
 * @returns Formatted string (e.g., "4.5 MB", "1.2 GB")
 */
export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  // Round to 1 decimal place
  const value = parseFloat((bytes / Math.pow(k, i)).toFixed(1));

  return `${value} ${sizes[i]}`;
};

/**
 * Format employment/education period (from predocs - used by History components)
 * @returns Formatted period string (e.g., "Jan 2020 — Dec 2023", "Jan 2020 — Present")
 */
export const formatPeriod = (
  fromMonth?: string | null,
  fromYear?: string | null,
  toMonth?: string | null,
  toYear?: string | null,
) => {
  const from = [fromMonth, fromYear].filter(Boolean).join(" ");
  const to = [toMonth, toYear].filter(Boolean).join(" ");
  if (from && to) return `${from} — ${to}`;
  if (from) return `${from} — Present`;
  if (to) return `Until ${to}`;
  return "Period not specified";
};

/**
 * Build metadata object from form fields and form data (matches legacy Utility.getMetadata)
 * Includes ALL fields (Internal and user-entered) as per legacy implementation
 * @param formFields - Array of form fields from FormSchema
 * @param formData - User-entered form data
 * @returns Metadata object ready for upload
 */
export const buildDocumentMetadata = (
  formFields: Array<{
    name: string;
    type: string;
    defaultValue?: string | number | boolean | Date | null;
    value?: string | number | boolean | { results: any[] } | null;
    copy?: boolean;
    digitonly?: boolean;
    prefix?: string;
    defaultChecked?: boolean;
    default?: string | number;
    multi?: boolean;
  }>,
  formData: Record<
    string,
    string | number | boolean | Date | { results: any[] } | null
  >,
): Record<string, any> => {
  const metadata: Record<string, any> = {};

  formFields.forEach((field) => {
    const column = field.name;

    if (field.type === "Internal") {
      // Internal fields: use field.value directly (from FormJSON)
      // If copy == true, copy value from formData[field.value] instead
      if (
        field.copy === true &&
        typeof field.value === "string" &&
        formData[field.value] !== undefined &&
        formData[field.value] !== null
      ) {
        metadata[column] = formData[field.value];
      } else {
        // Value may contain placeholders like {UserID}, {UserEmail}, etc. that will be replaced later
        // Value may be a simple value or complex object (e.g., { results: [...] })
        metadata[column] = field.value;
      }
    } else if (field.type === "Text") {
      if (formData[field.name]) {
        let fieldValue = formData[field.name];
        // Strip non-digit characters if digitonly is true
        if (field.digitonly) {
          fieldValue = String(fieldValue).replace(/\D/g, "");
        }
        // Prepend prefix if provided
        if (field.prefix) {
          fieldValue = field.prefix + fieldValue;
        }
        metadata[column] = fieldValue;
      }
    } else if (field.type === "Note") {
      if (formData[field.name]) {
        let fieldValue = formData[field.name];
        // Prepend prefix if provided
        if (field.prefix) {
          fieldValue = field.prefix + fieldValue;
        }
        metadata[column] = fieldValue;
      }
    } else if (field.type === "Number" || field.type === "Currency") {
      // Number and Currency fields: ensure numeric type
      if (formData[field.name] !== undefined && formData[field.name] !== null) {
        const numValue = Number(formData[field.name]);
        if (!isNaN(numValue)) {
          metadata[column] = numValue;
        } else {
          // Keep original value if conversion fails (backend will validate)
          metadata[column] = formData[field.name];
        }
      }
    } else if (field.type === "Date" || field.type === "DateTime") {
      if (formData[field.name]) {
        // Convert Date to ISO string (matches legacy behavior)
        const dateValue = formData[field.name];
        if (dateValue instanceof Date) {
          metadata[column] = dateValue.toISOString();
        } else if (typeof dateValue === "string") {
          // Try to parse and convert to ISO
          const parsedDate = new Date(dateValue);
          if (!isNaN(parsedDate.getTime())) {
            metadata[column] = parsedDate.toISOString();
          } else {
            metadata[column] = dateValue;
          }
        } else {
          metadata[column] = dateValue;
        }
      }
    } else if (field.type === "Person" || field.type === "PeoplePicker") {
      // Person and PeoplePicker fields: use formData value directly
      if (formData[field.name]) {
        metadata[column] = formData[field.name];
      }
    } else if (field.type === "Boolean" || field.type === "Checkbox") {
      // Boolean fields: use formData value, or defaultChecked if null/undefined
      if (formData[field.name] !== undefined && formData[field.name] !== null) {
        metadata[column] = formData[field.name];
      } else if (field.defaultChecked !== undefined) {
        metadata[column] = field.defaultChecked;
      }
    } else if (
      field.type === "List" ||
      field.type === "Dropdown" ||
      field.type === "Choice"
    ) {
      // List fields: use formData value, or default if nothing selected
      if (formData[field.name]) {
        // Multi-select lists: legacy code doesn't handle them, so we'll just include the value
        // Single value case: use formData value
        metadata[column] = formData[field.name];
      } else if (field.default !== undefined && field.default !== null) {
        // If nothing is selected and a default value exists, use it
        metadata[column] = field.default;
      }
    } else {
      // Default: include if present in formData
      if (formData[field.name] !== undefined && formData[field.name] !== null) {
        metadata[column] = formData[field.name];
      }
    }
  });

  return metadata;
};

/**
 * Replace context values in metadata string (matches legacy Utility.replaceContextValues)
 * Replaces placeholders like {UserID}, {UserEmail}, etc. with actual user values
 * @param metadataString - JSON stringified metadata
 * @param user - User object from UserContext
 * @param fileName - File name to replace {FileName}
 * @returns Metadata string with context values replaced
 */
export const replaceContextValues = (
  metadataString: string,
  user: {
    ID?: number;
    SPID?: number;
    Email?: string;
    Region?: string;
    RegionID?: number;
    Role?: string;
    LoginName?: string;
  } | null,
  fileName: string,
): string => {
  if (!user) return metadataString;

  let result = metadataString;

  // Replace {UserID} with SPID (SharePoint user ID) - used for Person/Group fields
  if (user.SPID) {
    result = result.replace(new RegExp('"{UserID}"', "g"), String(user.SPID));
  }

  // Replace UserRegionID (numeric, quoted in JSON) - matches legacy implementation
  if (user.RegionID !== undefined && user.RegionID !== null) {
    result = result.replace(
      new RegExp('"{UserRegionID}"', "g"),
      String(user.RegionID),
    );
  }

  // Replace UserAccount (remove domain prefix)
  if (user.LoginName) {
    const userAccount = user.LoginName.replace(/^positive\\/i, "");
    result = result.replace(new RegExp("{UserAccount}", "g"), userAccount);
  }

  // Replace UserEmail
  if (user.Email) {
    result = result.replace(new RegExp("{UserEmail}", "g"), user.Email);
  }

  // Replace UserRegion
  if (user.Region) {
    result = result.replace(new RegExp("{UserRegion}", "g"), user.Region);
  }

  // Replace UserRole
  if (user.Role) {
    result = result.replace(new RegExp("{UserRole}", "g"), user.Role);
  }

  // Replace FileName (done separately in legacy code)
  result = result.replace(new RegExp("{FileName}", "g"), fileName);

  return result;
};

export type HeaderFilterPrimitive = string | number | boolean;

export type HeaderFilterValue =
  | HeaderFilterPrimitive
  | HeaderFilterPrimitive[]
  | null
  | undefined;

export interface HeaderFilterCriteria {
  [fieldName: string]: HeaderFilterValue;
}

export const applyHeaderFilters = <T extends Record<string, unknown>>(
  items: T[],
  filters: HeaderFilterCriteria,
): T[] => {
  const activeFilters = Object.entries(filters).filter(([, value]) => {
    if (value === null || value === undefined) {
      return false;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    if (typeof value === "string") {
      return value.trim().length > 0;
    }

    return true;
  });

  if (activeFilters.length === 0) {
    return items;
  }

  return items.filter((item) => {
    return activeFilters.every(([fieldName, filterValue]) => {
      const itemValue = (item as Record<string, unknown>)[fieldName];

      if (Array.isArray(filterValue)) {
        const candidates = filterValue.map((entry) => String(entry));
        return candidates.includes(String(itemValue));
      }

      if (typeof filterValue === "string") {
        const text = filterValue.trim().toLowerCase();
        if (!text) {
          return true;
        }
        return String(itemValue ?? "")
          .toLowerCase()
          .includes(text);
      }

      return itemValue === filterValue;
    });
  });
};

export const getDistinctFieldValues = <
  T extends Record<string, unknown>,
  K extends keyof T,
>(
  items: T[],
  fieldName: K,
): string[] => {
  const values = new Set<string>();

  items.forEach((item) => {
    const raw = item[fieldName];

    if (raw === null || raw === undefined) {
      return;
    }

    if (Array.isArray(raw)) {
      raw.forEach((entry) => {
        const text = String(entry).trim();
        if (text) {
          values.add(text);
        }
      });
    } else {
      const text = String(raw).trim();
      if (text) {
        values.add(text);
      }
    }
  });

  return Array.from(values).sort((a, b) => a.localeCompare(b));
};
